import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { useCanViewAllTasks } from '../hooks/useRoleAccess';
import { taskAPI, clientGroupAPI, workspaceAPI, projectAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { useLiveInvalidate } from '../lib/useLiveData';
import { AssigneeAvatars } from '../components/AssigneeAvatars';
import { formatDate, localDateStr, weekRangeMonSun, monthRange } from '../lib/dateFormat';
import { getTodayLocal, getLocalDateString, isoToLocalDateString } from '../utils/date';
import { TaskDetailSheet } from '../components/TaskDetailSheet';
import { TaskCreateModal } from '../components/TaskCreateModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { StellarSelect } from '../components/StellarFormControls';
import { TaskStatusIndicator } from '../components/TaskStatusIndicator';
import { toast } from 'sonner';
import {
  Plus, Search, ChevronRight, ChevronDown, Loader2, Circle, Lock,
  Users, Building2, Filter, X, Bell, Eye, AlertTriangle, List, Columns3, CheckCircle2
} from 'lucide-react';
// RD3 — @dnd-kit/core migration. Replaces native HTML5 drag-and-drop with a
// keyboard-accessible alternative. PointerSensor handles mouse/touch; the
// KeyboardSensor lets users Tab to a card, press Space to pick it up, arrow
// keys to move between columns, Space again to drop, Esc to cancel.
import {
  DndContext, useDroppable, useDraggable, PointerSensor, KeyboardSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';

// RD3 — Kanban column droppable wrapper.
function BoardDroppableColumn({ id, className, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'ring-2 ring-gold/60 ring-offset-1 ring-offset-background' : ''}`}
      data-testid={`board-column-${id}`}
    >
      {children}
    </div>
  );
}

// RD3 — Kanban draggable card. Inherits all D54 B2 click-vs-drag logic via
// the inner onClick handler. Pointer activation needs 5px to trigger drag
// (matches the sensor's activationConstraint).
function BoardDraggableCard({ id, onOpen, children, className }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={onOpen}
      data-testid={`board-task-card-${id}`}
      className={`${className} ${isDragging ? 'opacity-50' : ''} focus:outline-none focus:ring-2 focus:ring-gold/60`}
    >
      {children}
    </div>
  );
}


// D87 add-on — pending-transfer bell badge. Shown on a task row when the
// current viewer is the RECEIVER of a pending transfer, so they can open
// the task and accept/decline in one click instead of digging through the
// notifications panel. Pulses subtly to draw the eye without being noisy.
// Legacy-subtask badge — "(N subtasks)" chip on parent rows (2026-07 collapse).
function SubtaskCountBadge({ task }) {
  const n = task?.legacy_subtask_count;
  if (!n) return null;
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30 shrink-0 whitespace-nowrap"
          data-testid={`subtask-count-${task.id}`}>
      {n} subtask{n === 1 ? '' : 's'}
    </span>
  );
}

function PendingTransferBell({ task, currentUserId }) {
  const p = task?.pending_transfer;
  if (!p || p.status !== 'pending' || p.to_user_id !== currentUserId) return null;
  return (
    <span
      className="relative inline-flex shrink-0 ml-1"
      title={`Transfer requested by ${p.requested_by_name || ''}: ${p.reason || ''}`}
      data-testid={`task-pending-transfer-bell-${task.id}`}
    >
      <span className="absolute inline-flex h-full w-full rounded-full bg-gold/50 opacity-75 animate-ping" />
      <span className="relative inline-flex items-center justify-center h-4 w-4 rounded-full bg-gold/20 border border-gold/60">
        <Bell className="h-2.5 w-2.5 text-gold" />
      </span>
    </span>
  );
}


// 2026-07 REDESIGN — top navigation collapsed to 3 tabs:
// My Tasks / Team / By Client. "All" folded into Team, "By Status" became
// the Group-by: Status filter, "Following" lives inside My Tasks as the
// "Watching" section.
const VIEWS_ALL = [
  { id: 'my', label: 'My Tasks', icon: Circle },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'client', label: 'By Client', icon: Building2 },
];
const VIEWS_MANAGER = [
  { id: 'my', label: 'My Tasks', icon: Circle },
  { id: 'team_mine', label: 'Team', icon: Users },
  { id: 'client', label: 'By Client', icon: Building2 },
];
const VIEWS_TEAM = [
  { id: 'my', label: 'My Tasks', icon: Circle },
  { id: 'client', label: 'By Client', icon: Building2 },
];

// BUG-T-002/003/005/T-008 (2026-05-21) — Tasks tabs ↔ URL paths.
// 2026-07 REDESIGN — legacy paths (/tasks/all, /tasks/by-status,
// /tasks/following) map to the nearest surviving tab so old deep-links
// keep working.
const VIEW_PATH_BY_ID = {
  my: '/tasks/my-tasks',
  team: '/tasks/team',
  client: '/tasks/by-client',
  // Manager-only "team_mine" doesn't get a dedicated URL — it lives inside
  // /tasks/team for shareability.
  team_mine: '/tasks/team',
};
const VIEW_ID_BY_PATH = {
  '/tasks/my-tasks': 'my',
  '/tasks/team': 'team',
  '/tasks/all': 'team',
  '/tasks/by-client': 'client',
  '/tasks/by-status': 'team',
  '/tasks/following': 'my',
};

const GROUP_OPTIONS = [
  { id: 'none', label: 'Group: None' },
  { id: 'status', label: 'Group: Status' },
  { id: 'priority', label: 'Group: Priority' },
];
const GROUP_IDS = GROUP_OPTIONS.map(g => g.id);

const STATUS_DOT = { not_started: 'bg-foreground/15', in_progress: 'bg-gold', waiting: 'bg-amber-400', review: 'bg-purple-400', completed: 'bg-green-500', cancelled: 'bg-red-500/40' };
const PRIORITY_DOT = { high: 'bg-red-500/70', medium: 'bg-gold/70', low: 'bg-foreground/20' };
// 2026-07-31 — 'Waiting' removed as a user-facing status. Attendance-paused
// tasks (still 'waiting' internally) are grouped under In Progress so they
// never fall off the board.
const BOARD_COLS = [
  { status: 'not_started', label: 'Not Started', dot: 'bg-slate-400', bg: 'bg-slate-500/[0.03]', border: 'border-slate-500/20' },
  { status: 'in_progress', label: 'In Progress', dot: 'bg-gold', bg: 'bg-gold/[0.03]', border: 'border-gold/20' },
  { status: 'review', label: 'Review', dot: 'bg-purple-400', bg: 'bg-purple-500/[0.03]', border: 'border-purple-500/20' },
  { status: 'completed', label: 'Completed', dot: 'bg-green-500', bg: 'bg-green-500/[0.03]', border: 'border-green-500/20' },
];

export default function UnifiedTasksPage() {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  const canViewAll = useCanViewAllTasks();
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 2 Group B: derive role classes from workspaceRole. super_admin
  // counts as elevated even when workspaceRole is unset (matches backend).
  const isManager = workspaceRole === 'manager';
  // Tab set selection. Elevated → full; Manager → My / By Client / My Team;
  // Team (and any fallback) → My / By Client only (no "By Status" — B5).
  const VIEWS = useMemo(() => {
    if (canViewAll) return VIEWS_ALL;
    if (isManager) return VIEWS_MANAGER;
    return VIEWS_TEAM;
  }, [canViewAll, isManager]);
  const VIEW_IDS = useMemo(() => new Set(VIEWS.map(v => v.id)), [VIEWS]);
  const wsId = currentWorkspace?.id;

  // B1 — Default landing tab is "By Client" for all roles. Per-user
  // localStorage key (keyed by user.id) so signing in on a shared device
  // never leaks one user's last-viewed tab to another account.
  const viewKey = `stellar_tasks_view_${user?.id || 'guest'}`;
  // Legacy migration: the old global key existed for everyone; if present
  // and the new per-user key is empty, copy across once.
  const [view, setView] = useState(() => {
    try {
      // BUG-T-002/003/005 — pathname wins on first paint. Avoids a flash
      // of the persisted tab before the useEffect path-sync runs.
      const fromPath = VIEW_ID_BY_PATH[typeof window !== 'undefined' ? window.location.pathname : ''];
      if (fromPath) return fromPath;
      const perUser = localStorage.getItem(viewKey);
      if (perUser) return perUser;
      const legacy = localStorage.getItem('stellar_tasks_view');
      if (legacy) {
        localStorage.setItem(viewKey, legacy);
        return legacy;
      }
    } catch {}
    // First-time user: land on "By Client" per spec.
    try { localStorage.setItem(viewKey, 'client'); } catch {}
    return 'client';
  });
  // If saved view is hidden for this user (e.g. Manager landing on a 'team'
  // or 'all' bookmark, Team landing on 'status'), redirect to 'my' as soon
  // as we know the role. localStorage is corrected too.
  useEffect(() => {
    if (!VIEW_IDS.has(view)) {
      setView('my');
      try { localStorage.setItem(viewKey, 'my'); } catch {}
    }
  }, [VIEW_IDS, view, viewKey]);
  const [groupBy, setGroupBy] = useState(() => {
    const g = localStorage.getItem('stellar_tasks_group');
    return GROUP_IDS.includes(g) ? g : 'none';
  });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  // 2026-09 — global search: backend-powered, finds every permitted task.
  const [globalResults, setGlobalResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  // 2026-07 — Overdue strip: collapsed by default on every tab.
  const [overdueOpen, setOverdueOpen] = useState(false);
  // 2026-07 — List (default) / Board view toggle, persisted per browser.
  const [viewMode, setViewMode] = useState(() => (localStorage.getItem('stellar_tasks_viewmode') === 'board' ? 'board' : 'list'));
  const setViewModePersist = (m) => { setViewMode(m); localStorage.setItem('stellar_tasks_viewmode', m); };
  // 2026-07 — "Completed" chip: all completed tasks, ignores time window.
  const [showCompleted, setShowCompleted] = useState(false);
  // 2026-07-13 — notification deep-link into a version thread:
  // /tasks/all?task=X&submission=Y&comment=Z expands + scrolls to it.
  const [focusSubmission, setFocusSubmission] = useState(null);
  const [focusComment, setFocusComment] = useState(null);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  // Batch 3 B2 — Persisted accordion collapse state for the By-Client view.
  // Keyed by client_group_id (or 'none' for unscoped). Default = all collapsed
  // (set lazily when a client list first arrives in the client view).
  // Per-user storage key prevents cross-account leak.
  const clientCollapseKey = user?.id ? `stellar_tasks_client_collapse_${user.id}` : 'stellar_tasks_client_collapse_anon';
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(clientCollapseKey) || '{}'); }
    catch { return {}; }
  });
  const [clientCollapseSeeded, setClientCollapseSeeded] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [nextCursorId, setNextCursorId] = useState(null);
  const [totalTasks, setTotalTasks] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Quick Create — B8 modal is self-contained
  const [showCreate, setShowCreate] = useState(false);
  // Batch 5 R2 B-Block CAL-007 — when the Calendar empty-day "+ New" button
  // navigates here with `?due_date=YYYY-MM-DD`, we forward the date into the
  // TaskCreateDialog as the default value (no UI surface — purely state).
  const [createDefaultDueDate, setCreateDefaultDueDate] = useState('');

  // Auto-open the New Task modal when the URL has ?new=true
  // (used by Dashboard, Kanban, Projects "+ New Task" buttons that navigate here).
  // Also honour BUG-D04/D05: ?filter=overdue and ?filter=due_soon, applied
  // by the dashboard's "View all overdue/due-soon →" widget links.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    let mutated = false;
    const next = new URLSearchParams(searchParams);
    if (searchParams.get('new') === 'true') {
      setShowCreate(true);
      // Batch 5 R2 B-Block CAL-007 — pick up `due_date` for Calendar empty-day click flow.
      const dueDateParam = searchParams.get('due_date');
      if (dueDateParam && /^\d{4}-\d{2}-\d{2}$/.test(dueDateParam)) {
        setCreateDefaultDueDate(dueDateParam);
        next.delete('due_date');
      }
      next.delete('new');
      mutated = true;
    }
    // Transfer fix (2026-06) — notification deep-link `?task=<id>` opens the
    // task detail sheet directly (TaskDetailSheet fetches by id, so this works
    // even when the task is not yet in the caller's visible list — e.g. an
    // incoming transfer the receiver must accept).
    const taskParam = searchParams.get('task');
    if (taskParam) {
      setSelectedTaskId(taskParam);
      // Version-thread deep-link (from "changes requested" / comment notifs)
      const subParam = searchParams.get('submission');
      const commentParam = searchParams.get('comment');
      if (subParam) {
        setFocusSubmission(subParam);
        next.delete('submission');
      }
      if (commentParam) {
        setFocusComment(commentParam);
        next.delete('comment');
      }
      next.delete('task');
      mutated = true;
    }
    const filterParam = searchParams.get('filter');
    if (filterParam === 'overdue' || filterParam === 'due_soon') {
      // Overdue lives in the collapsible strip on every tab; due_soon uses
      // the rolling 7-day window the "This Week" pill encodes.
      setViewPersist(canViewAll ? 'team' : 'my');
      setTimeWindowPersist(filterParam === 'due_soon' ? 'week' : 'all');
      if (filterParam === 'overdue') setOverdueOpen(true);
      next.delete('filter');
      mutated = true;
    }
    if (mutated) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem(viewKey, v); } catch {}
    // BUG-T-008 — sync the URL so each tab has a unique deep-link.
    const target = VIEW_PATH_BY_ID[v] || '/tasks/all';
    if (location.pathname !== target) navigate(target, { replace: true });
  };
  const setGroupPersist = (g) => { setGroupBy(g); localStorage.setItem('stellar_tasks_group', g); };

  // BUG-T-002/003/005 — on mount, if the pathname maps to a known view,
  // prefer it over the localStorage-remembered tab. Lets external links
  // and direct nav (/tasks/by-status, /tasks/by-client, etc.) land on
  // the right tab.
  // Batch 2 R2 / D-012 / D-016 / D-017 (2026-05-22) — also accept ?tab=<id>
  // query param on /tasks/all so Dashboard widget links of the form
  // /tasks/all?tab=by-status pre-select the right tab without needing a
  // dedicated path route.
  useEffect(() => {
    const pathView = VIEW_ID_BY_PATH[location.pathname];
    const tabParam = searchParams.get('tab');
    const tabAlias = { 'my-tasks': 'my', 'by-client': 'client', 'by-status': 'team', 'team': 'team', 'all': 'team', 'following': 'my' };
    const queryView = tabParam ? (tabAlias[tabParam] || tabParam) : null;
    // Legacy ?tab=by-status deep-links now land on the Board view toggle.
    if (tabParam === 'by-status') setViewModePersist('board');
    const resolved = queryView || pathView;
    if (resolved && VIEW_IDS.has(resolved) && resolved !== view) {
      setView(resolved);
      try { localStorage.setItem(viewKey, resolved); } catch {}
    }
  }, [location.pathname, searchParams, VIEW_IDS]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Time-window filter (Day/Week/Month/All) — Iter 62 Bug #3 fix ──
  // Now PER-TAB persisted. Each top-level tab (my / team / all / client /
  // status) remembers its own pill in localStorage. The "All" tab defaults
  // to 'all' (no time-window filter) so no-due-date tasks are reachable.
  // All other tabs default to 'week'.
  const TW_VALID = ['day', 'week', 'month', 'all'];
  const TW_DEFAULT_BY_TAB = { my: 'week', team: 'week', team_mine: 'week', client: 'week' };
  const tw_key_for = (uid, tab) => `stellar_tasks_tw_${uid || 'guest'}_${tab}`;
  // Legacy global key (pre-Iter 62) — kept for one-time migration only.
  const tw_legacy_key = `stellar_tasks_timewindow_${user?.id || 'guest'}`;

  const initialTab = (typeof window !== 'undefined' && localStorage.getItem(viewKey)) || 'client';
  const [timeWindow, setTimeWindow] = useState(() => {
    if (typeof window === 'undefined') return TW_DEFAULT_BY_TAB[initialTab] || 'week';
    const perTab = localStorage.getItem(tw_key_for(null, initialTab));
    if (TW_VALID.includes(perTab)) return perTab;
    return TW_DEFAULT_BY_TAB[initialTab] || 'week';
  });
  // When user.id hydrates OR the active tab changes, look up that tab's
  // stored window (falling back to the legacy global key the first time
  // for graceful migration), or use the per-tab default.
  useEffect(() => {
    if (!user?.id) return;
    const perTab = localStorage.getItem(tw_key_for(user.id, view));
    if (TW_VALID.includes(perTab)) {
      if (perTab !== timeWindow) setTimeWindow(perTab);
      return;
    }
    // First time on this tab post-Iter-62: try legacy global value, then default.
    const legacy = localStorage.getItem(tw_legacy_key);
    const next = (TW_VALID.includes(legacy) && view !== 'all')
      ? legacy
      : (TW_DEFAULT_BY_TAB[view] || 'week');
    if (next !== timeWindow) setTimeWindow(next);
  }, [view, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setTimeWindowPersist = (w) => {
    setTimeWindow(w);
    if (user?.id) localStorage.setItem(tw_key_for(user.id, view), w);
  };

  // Phase 2 Group A — local-TZ today + Mon-Sun and full-month ranges
  // (recomputed each render — cheap). getLocalDateString uses local methods
  // so it works for users in Saskatchewan and India alike.
  const todayLocal = getLocalDateString(getTodayLocal());
  const [weekStart, weekEnd] = weekRangeMonSun();
  const [monthStart, monthEnd] = monthRange();

  // Phase 2 Group B4 — direct reports list for Manager's "My Team" tab.
  // Owner/Admin do not need this (they have a real "Team" tab). Team role
  // never sees this tab. Endpoint is auth-scoped server-side: a Manager only
  // ever gets their own direct reports' user_ids.
  const [directReports, setDirectReports] = useState(null); // null = unknown, [] = none
  useEffect(() => {
    if (!wsId) return;
    if (!isManager) { setDirectReports([]); return; }
    workspaceAPI.myDirectReports(wsId)
      .then(r => setDirectReports(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDirectReports([]));
  }, [wsId, isManager]);

  // Fetch tasks.
  //
  // D88 PROD FIX (2026-02): The 15-second background `setInterval` poll
  // used to call this with `setLoading(true)` every cycle, which flipped
  // the page-level loading spinner ON for the duration of the fetch.
  // The loading branch unmounts the entire task-list subtree (line ~718)
  // — and any open TaskDetailSheet's textarea + AI review badge state
  // perceptibly "flashed", because the parent re-rendered and the DOM
  // layout reflowed.
  //
  // `silent=true` is now used by the background poll path and the D84 WS
  // invalidate path. Only the initial / explicit refetches show the
  // spinner. The poll cadence is unchanged — the user just no longer
  // sees the page flash every 15s.
  const fetchTasks = useCallback(async ({ silent = false } = {}) => {
    if (!wsId) return;
    if (!silent) setLoading(true);
    try {
      const filters = { limit: 200 };
      if (view === 'my') filters.assigned_to = user?.id;
      const res = await taskAPI.list(wsId, filters);
      setTasks(res.data || []);
      setNextCursor(res._pagination?.next_cursor || null);
      setNextCursorId(res._pagination?.next_cursor_id || null);
      setTotalTasks(res._pagination?.total || (res.data || []).length);
    } catch {}
    if (!silent) setLoading(false);
  }, [wsId, view, user?.id]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  // BUG-07 — listen for global task-changed events so approve/complete from
  // anywhere in the app refreshes the list without a page reload. 15-second
  // poll fallback for changes from other tabs / devices.
  //
  // D88 PROD FIX (2026-02): both the event listener AND the setInterval
  // fallback refetch in `silent` mode now — no spinner flicker.
  useEffect(() => {
    const onChanged = () => fetchTasks({ silent: true });
    window.addEventListener('stellar-task-changed', onChanged);
    const pollId = setInterval(onChanged, 15_000);
    return () => {
      window.removeEventListener('stellar-task-changed', onChanged);
      clearInterval(pollId);
    };
  }, [fetchTasks]);
  // D84 — Real-time push from other members: refetch on every `tasks`
  // invalidate the workspace WS sends (their assignment / status change /
  // create / delete). Polling above still runs as fallback when WS drops.
  // D88 PROD FIX (2026-02): silent refetch — no spinner flicker.
  useLiveInvalidate('tasks', useCallback(() => fetchTasks({ silent: true }), [fetchTasks]));

  // Fetch members, clients, projects
  useEffect(() => {
    if (!wsId) return;
    // 2026-06-05 PROD HOTFIX — use the un-scoped assignee picker endpoint
    // (assignee_picker_unscoped) so Manager / Team users get the full
    // roster for task creation + assignment. /members is still admin-gated
    // for the management surfaces; the picker has its own endpoint now.
    workspaceAPI.listAssignableMembers(wsId).then(r => setMembers((r.data || []).map(m => m.user ? { ...m.user, role: m.role } : null).filter(Boolean))).catch(() => {});
    clientGroupAPI.list(wsId).then(r => setClients(sortClientsAlpha(r.data || []))).catch(() => {});
    projectAPI.list(wsId).then(r => setProjects(r.data || [])).catch(() => {});
  }, [wsId]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const filters = { limit: 200, cursor: nextCursor, cursor_id: nextCursorId };
      if (view === 'my') filters.assigned_to = user?.id;
      const res = await taskAPI.list(wsId, filters);
      setTasks(prev => [...prev, ...(res.data || [])]);
      setNextCursor(res._pagination?.next_cursor || null);
      setNextCursorId(res._pagination?.next_cursor_id || null);
    } catch {}
    setLoadingMore(false);
  };

  // Filter tasks by view
  // BUG-10 (2026-05-23) — By Client tab now respects an explicit scope
  // (my / team / all). Owner/Admin default to 'all'. Manager defaults to
  // 'team'. Team defaults to 'my'. Persisted per-user in localStorage.
  const clientScopeKey = `stellar_tasks_client_scope_${user?.id || 'guest'}`;
  const [clientScope, setClientScope] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    return localStorage.getItem(clientScopeKey) || (canViewAll ? 'all' : isManager ? 'team' : 'my');
  });
  const setClientScopePersist = (s) => {
    setClientScope(s);
    if (user?.id) localStorage.setItem(clientScopeKey, s);
  };
  const viewTasks = tasks.filter(t => {
    if (searchQ && !t.title?.toLowerCase().includes(searchQ.toLowerCase())) return false;
    // 2026-07 — "Completed" chip: only completed tasks when active.
    if (showCompleted && t.status !== 'completed') return false;
    if (view === 'my') {
      // Transfer fix (2026-06) — incoming pending transfers count as "mine"
      // so the receiver sees the task (with the pulsing bell) before accepting.
      const incoming = t.pending_transfer?.status === 'pending' && t.pending_transfer?.to_user_id === user?.id;
      // 2026-07 — My Tasks = ONLY directly-assigned tasks (Watching removed;
      // Accountable-to routes through the Reviews queue instead).
      return (t.assigned_to || []).includes(user?.id) || incoming;
    }
    // Phase 2 Group B4 — Manager's "My Team" tab: tasks assigned to direct
    // reports only (excludes tasks assigned solely to the manager themselves).
    // The backend already restricts the dataset to "self + reports" via
    // _get_visible_assignee_ids; this is the additional UI scope.
    if (view === 'team_mine') {
      if (!directReports || directReports.length === 0) return false;
      return (t.assigned_to || []).some(uid => directReports.includes(uid));
    }
    // BUG-10 — By Client tab honors the scope selector. Owner/Admin default
    // 'all' = no filter; 'my' = current user only; 'team' = any teammate.
    if (view === 'client') {
      if (clientScope === 'my') return (t.assigned_to || []).includes(user?.id);
      if (clientScope === 'team') return (t.assigned_to || []).length > 0;
      return true;
    }
    return true; // team shows everything
  });

  // ── Day / Week / Month time-window filter ──
  // Overdue = past-due AND not completed/cancelled. Pinned across all 3 views.
  // Phase 2 Group B3 — compare due_date in user's LOCAL timezone (not UTC
  // via .slice). Fixes the bug where India users had tasks slip into "last
  // week" because new Date(iso).toISOString() shifted them.
  const dueLocal = (t) => (t.due_date ? isoToLocalDateString(t.due_date) : '');
  const overdueTasks = viewTasks
    .filter(t => { const d = dueLocal(t); return d && d < todayLocal && t.status !== 'completed' && t.status !== 'cancelled'; })
    .sort((a, b) => dueLocal(a).localeCompare(dueLocal(b))); // oldest = most overdue first
  const overdueIds = new Set(overdueTasks.map(t => t.id));

  // In-window = tasks whose due_date is inside [start, end] for the chosen window.
  // Tasks with no due_date are excluded; surfaced via noDueDateCount hint.
  // Overdue tasks are NOT duplicated into the window list (the pinned section already shows them).
  // Iter 62 Bug #3: 'all' window short-circuits — every task passes, including no-due-date.
  const windowMatch = (t) => {
    // Completed chip shows ALL completed tasks regardless of time window
    // (their due dates are usually in the past).
    if (showCompleted) return true;
    if (timeWindow === 'all') return !overdueIds.has(t.id);
    if (!t.due_date) return false;
    if (overdueIds.has(t.id)) return false;
    const d = dueLocal(t);
    if (timeWindow === 'day')   return d === todayLocal;
    if (timeWindow === 'week')  return d >= weekStart && d <= weekEnd;
    if (timeWindow === 'month') return d >= monthStart && d <= monthEnd;
    return true;
  };
  const windowFiltered = viewTasks.filter(windowMatch);
  const noDueDateCount = viewTasks.filter(t => !t.due_date && t.status !== 'completed' && t.status !== 'cancelled').length;
  const windowEmpty = overdueTasks.length === 0 && windowFiltered.length === 0;
  // Phase 2 Group B3 — count badge reflects what is actually rendered after
  // the time-window + overdue logic, not the unfiltered viewTasks length.
  const renderedCount = overdueTasks.length + windowFiltered.length;

  // 2026-07 REDESIGN — structural sections per tab. The Group-by filter
  // (none / priority) renders INSIDE each section; Group-by 'status'
  // switches the whole content area to the kanban board.
  const getSections = () => {
    const source = windowFiltered;
    if (view === 'my') {
      // 2026-07 — My Tasks = single flat section (Watching removed).
      return [{ key: 'all', label: 'My Tasks', tasks: source }];
    }
    if (view === 'team' || view === 'team_mine') {
      const byUser = {};
      source.forEach(t => {
        const assignees = t.assigned_to || [];
        if (assignees.length === 0) {
          (byUser['unassigned'] = byUser['unassigned'] || []).push(t);
        } else {
          assignees.forEach(uid => { (byUser[uid] = byUser[uid] || []).push(t); });
        }
      });
      return Object.entries(byUser).map(([uid, tasks]) => {
        const m = members.find(u => u.id === uid);
        return { key: uid, label: m?.name || (uid === 'unassigned' ? 'Unassigned' : uid.slice(0, 8)), tasks };
      }).sort((a, b) => b.tasks.length - a.tasks.length);
    }
    if (view === 'client') {
      const byClient = {};
      source.forEach(t => {
        const cid = t.client_group_id || 'none';
        (byClient[cid] = byClient[cid] || []).push(t);
      });
      return Object.entries(byClient).map(([cid, tasks]) => {
        const c = clients.find(x => x.id === cid);
        return { key: cid, label: c?.name || (cid === 'none' ? 'No Client' : 'Unknown'), tasks };
      }).sort((a, b) => a.key === 'none' ? 1 : b.key === 'none' ? -1 : b.tasks.length - a.tasks.length);
    }
    return [{ key: 'all', label: 'All Tasks', tasks: source }];
  };

  const sections = getSections();

  const byDueAsc = (a, b) => (dueLocal(a) || '9999-99-99').localeCompare(dueLocal(b) || '9999-99-99');
  // Due-date buckets: Today / This Week / Later (Later includes no-due-date).
  const bucketize = (list) => {
    const sorted = [...list].sort(byDueAsc);
    return [
      { key: 'today', label: 'Today', color: 'bg-gold', tasks: sorted.filter(t => dueLocal(t) === todayLocal) },
      { key: 'this_week', label: 'This Week', color: 'bg-foreground/30', tasks: sorted.filter(t => { const d = dueLocal(t); return d > todayLocal && d <= weekEnd; }) },
      { key: 'later', label: 'Later', color: 'bg-foreground/15', tasks: sorted.filter(t => { const d = dueLocal(t); return !d || d > weekEnd; }) },
    ].filter(b => b.tasks.length > 0);
  };
  const priorityGroups = (list) => ['high', 'medium', 'low'].map(p => ({
    key: p, label: `${p.charAt(0).toUpperCase() + p.slice(1)} Priority`, color: PRIORITY_DOT[p],
    tasks: [...list].filter(t => (t.priority || 'medium') === p).sort(byDueAsc),
  })).filter(g => g.tasks.length > 0);

  // Batch 3 B2 — Seed all client accordion rows as collapsed on first render
  // in the By-Client view (only when localStorage has no saved state).
  useEffect(() => {
    if (view !== 'client') return;
    if (clientCollapseSeeded) return;
    if (!sections || sections.length === 0) return;
    const stored = localStorage.getItem(clientCollapseKey);
    if (stored && stored !== '{}') {
      setClientCollapseSeeded(true);
      return;
    }
    const seed = {};
    sections.forEach(g => { seed[g.key] = true; });
    setCollapsed(prev => ({ ...prev, ...seed }));
    try { localStorage.setItem(clientCollapseKey, JSON.stringify(seed)); } catch {}
    setClientCollapseSeeded(true);
  }, [view, sections, clientCollapseSeeded, clientCollapseKey]);

  const toggleGroup = (key) => setCollapsed(prev => {
    const next = { ...prev, [key]: !prev[key] };
    // Batch 3 B2 — persist client-view collapse so refresh remembers
    if (view === 'client') {
      try { localStorage.setItem(clientCollapseKey, JSON.stringify(next)); } catch {}
    }
    return next;
  });

  // Board drag — RD3: migrated from native HTML5 DnD to @dnd-kit/core.
  // PointerSensor handles mouse/touch; KeyboardSensor adds full keyboard
  // operability (Space = pick up, Arrow keys = move, Space = drop, Esc = cancel).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const handleDragEnd = async ({ active, over }) => {
    if (!over || !active) return;
    const tid = String(active.id);
    const newStatus = String(over.id);
    // No-op if dropped on the same column.
    const current = tasks.find(t => t.id === tid);
    if (!current || current.status === newStatus) return;
    try {
      await taskAPI.update(wsId, tid, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === tid ? { ...t, status: newStatus } : t));
      toast.success(`Status → ${newStatus.replace('_', ' ')}`);
    } catch { toast.error('Failed'); }
  };

  // 2026-07 — shared task row (overdue strip + buckets + priority groups).
  const renderRow = (t) => {
    const isOverdueRow = t.due_date && isoToLocalDateString(t.due_date) < todayLocal && t.status !== 'completed' && t.status !== 'cancelled';
    return (
      <button key={t.id} onClick={() => setSelectedTaskId(t.id)} data-testid={`task-row-${t.id}`} className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left hover:bg-foreground/[0.02] border border-transparent ${isOverdueRow ? 'hover:border-red-500/20' : 'hover:border-border/20'} group`}>
        {t.status !== 'completed' && t.status !== 'cancelled' && (
          <>
            <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium}`} title={t.priority} />
            {t.priority_locked && <Lock className="h-2.5 w-2.5 text-muted-foreground/25 shrink-0" data-testid="priority-locked-icon" />}
          </>
        )}
        <span className="flex-1 min-w-0 flex flex-col">
          <span className="text-[13px] truncate text-foreground font-medium">{t.title}</span>
        </span>
        <PendingTransferBell task={t} currentUserId={user?.id} /><SubtaskCountBadge task={t} />
        {isOverdueRow && <span className="text-[8px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-semibold shrink-0">OVERDUE</span>}
        <TaskStatusIndicator status={t.status} size="sm" />
        {t.due_date && <span className="text-[11px] text-muted-foreground/40 ml-1">{formatDate(t.due_date)}</span>}
        <AssigneeAvatars assignees={t.assignee_details || t.assigned_to || []} members={members} max={2} />
      </button>
    );
  };

  // 2026-07 — single collapsible overdue strip, collapsed by default,
  // shared by the list views and the Group-by:Status board.
  const overdueStripEl = overdueTasks.length > 0 ? (
    <div className="rounded-xl border border-red-500/25 bg-red-500/[0.04]" data-testid="overdue-strip">
      <button onClick={() => setOverdueOpen(o => !o)} data-testid="overdue-strip-toggle" className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
        <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
        <span className="text-[13px] font-semibold text-red-400">{overdueTasks.length} overdue task{overdueTasks.length === 1 ? '' : 's'}</span>
        <span className="text-[11px] text-muted-foreground/50">— click to {overdueOpen ? 'collapse' : 'expand'}</span>
        <ChevronDown className={`h-4 w-4 text-red-400/70 ml-auto transition-transform duration-150 ${overdueOpen ? 'rotate-180' : ''}`} />
      </button>
      {overdueOpen && (
        <div className="px-2 pb-2 space-y-0.5" data-testid="overdue-strip-list">
          {overdueTasks.map(t => renderRow(t))}
        </div>
      )}
    </div>
  ) : null;

  // 2026-09 — debounced global search against /tasks/{ws}/search-tasks.
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2 || !currentWorkspace?.id) { setGlobalResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await taskAPI.searchTasks(currentWorkspace.id, q);
        setGlobalResults(res.data?.results || []);
      } catch { setGlobalResults([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ, currentWorkspace?.id]);

  // Shared filter controls — inline right of tabs on desktop, wrapped
  // second row on mobile (so nothing needs horizontal scrolling).
  const filterCluster = (
    <>
            {/* Time filter: Today / This Week / This Month / All */}
            <div className="flex items-center rounded-lg border border-border/20 bg-foreground/[0.02] p-0.5" data-testid="task-timewindow-toggle" title="Filter tasks by due date window. 'All' includes tasks with no due date.">
              {[
                { id: 'day', label: 'Today' },
                { id: 'week', label: 'This Week' },
                { id: 'month', label: 'This Month' },
                { id: 'all', label: 'All' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setTimeWindowPersist(opt.id)}
                  className={`px-2.5 py-1.5 sm:py-1 rounded-md text-[11px] font-medium transition-colors ${timeWindow === opt.id ? 'bg-gold/20 text-gold' : 'text-muted-foreground/50 hover:text-muted-foreground/80'}`}
                  data-testid={`task-timewindow-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* List / Board view toggle */}
            <div className="flex items-center rounded-lg border border-border/20 bg-foreground/[0.02] p-0.5" data-testid="task-viewmode-toggle">
              <button
                onClick={() => setViewModePersist('list')}
                className={`px-2.5 sm:px-2 py-1.5 sm:py-1 rounded-md transition-colors ${viewMode === 'list' ? 'bg-gold/20 text-gold' : 'text-muted-foreground/50 hover:text-muted-foreground/80'}`}
                title="List view"
                data-testid="task-viewmode-list"
              >
                <List className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setViewModePersist('board')}
                className={`px-2.5 sm:px-2 py-1.5 sm:py-1 rounded-md transition-colors ${viewMode === 'board' ? 'bg-gold/20 text-gold' : 'text-muted-foreground/50 hover:text-muted-foreground/80'}`}
                title="Board view (by status)"
                data-testid="task-viewmode-board"
              >
                <Columns3 className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Completed chip — all completed tasks, any tab, ignores window */}
            <button
              onClick={() => setShowCompleted(c => !c)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${showCompleted ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400' : 'border-border/20 bg-foreground/[0.02] text-muted-foreground/50 hover:text-muted-foreground/80'}`}
              data-testid="task-completed-chip"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Completed
            </button>
            {/* Group by: None / Priority — applies within any tab */}
            <StellarSelect
              value={groupBy}
              onValueChange={setGroupPersist}
              options={GROUP_OPTIONS.map(g => ({ value: g.id, label: g.label }))}
              size="sm"
              className="w-40"
              testId="task-groupby-select"
            />
            <span className="text-[11px] text-muted-foreground/30" data-testid="task-count-label">
              {view === 'team'
                ? `${viewTasks.length} task${viewTasks.length === 1 ? '' : 's'} across ${members.length} member${members.length === 1 ? '' : 's'}`
                : view === 'team_mine'
                  ? `${viewTasks.length} task${viewTasks.length === 1 ? '' : 's'} across ${directReports?.length || 0} direct report${(directReports?.length || 0) === 1 ? '' : 's'}`
                  : `${renderedCount} task${renderedCount === 1 ? '' : 's'}${totalTasks > viewTasks.length ? ` of ${totalTasks}` : ''}`}
            </span>
    </>
  );

  return (
    <div className="h-full flex flex-col" data-testid="unified-tasks-page">
      {/* Header — D53 mobile: top row wraps + tabs scroll horizontally */}
      <div className="border-b border-border/30 px-3 sm:px-6 py-3 shrink-0">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h1 className="text-[18px] font-display font-bold">Tasks</h1>
          <div className="flex items-center gap-2 flex-wrap order-3 sm:order-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <div className="flex items-center gap-2 h-9 px-3 rounded-lg border border-border/20 bg-foreground/[0.02] focus-within:border-gold/30 w-full">
                <Search className="h-3.5 w-3.5 text-muted-foreground/30" />
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search all tasks — title, client, people…" className="bg-transparent border-none outline-none text-[14px] sm:text-[12px] flex-1 min-w-0" data-testid="task-search-input" />
                {searching && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/40" />}
                {searchQ && <button onClick={() => setSearchQ('')} aria-label="Clear search" data-testid="task-search-clear"><X className="h-3 w-3 text-muted-foreground/30" /></button>}
              </div>
              {/* Global search results — finds EVERY permitted task (completed,
                  overdue, archived, old), regardless of tab/time filters. */}
              {searchQ.trim().length >= 2 && globalResults !== null && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border border-border/40 bg-card shadow-2xl max-h-[60vh] overflow-y-auto" data-testid="task-search-results">
                  {globalResults.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground/50 px-3 py-3" data-testid="task-search-empty">No tasks found for "{searchQ}"</p>
                  ) : globalResults.map(r => (
                    <button key={r.id} onClick={() => { setSelectedTaskId(r.id); }} className="w-full text-left px-3 py-2.5 hover:bg-foreground/[0.04] border-b border-border/10 last:border-0 flex flex-col gap-1" data-testid={`task-search-result-${r.id}`}>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-[12px] font-medium text-foreground truncate flex-1">{r.title}</span>
                        <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold shrink-0 ${r.state === 'overdue' ? 'bg-red-500/15 text-red-400' : r.state === 'completed' ? 'bg-emerald-500/15 text-emerald-400' : r.state === 'archived' ? 'bg-foreground/10 text-muted-foreground' : 'bg-gold/15 text-gold'}`}>{r.state.toUpperCase()}</span>
                      </span>
                      <span className="flex items-center gap-2 text-[10px] text-muted-foreground/50 flex-wrap">
                        {r.client_name && <span className="text-sky-300/70">{r.client_name}</span>}
                        <span className="capitalize">{(r.status || '').replace('_', ' ')}</span>
                        <span className="capitalize">{r.priority}</span>
                        {r.due_date && <span>Due {formatDate(r.due_date)}</span>}
                        {r.assignee_names.length > 0 && <span>→ {r.assignee_names.join(', ')}</span>}
                        {r.accountable_names.length > 0 && <span className="text-gold/60">Acc: {r.accountable_names.join(', ')}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" className="h-9 bg-gold/10 hover:bg-gold/20 text-gold border-0 text-[12px] shrink-0" onClick={() => setShowCreate(true)} data-testid="new-task-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> New Task
            </Button>
          </div>
        </div>
        {/* View tabs — mobile: horizontal scroll, no wrap */}
        <div className="flex items-center gap-1 overflow-x-auto sm:overflow-visible whitespace-nowrap snap-x [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {VIEWS.map(v => (
            <button key={v.id} onClick={() => setViewPersist(v.id)} data-testid={`task-view-${v.id}`} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors snap-start shrink-0 ${view === v.id ? 'bg-gold/15 text-gold' : 'text-muted-foreground/40 hover:text-muted-foreground/60 hover:bg-foreground/[0.02]'}`}>
              <v.icon className="h-3.5 w-3.5" /> {v.label}
            </button>
          ))}
          <div className="ml-auto hidden sm:flex items-center gap-2 shrink-0 pl-2">
            {filterCluster}
          </div>
        </div>
        {/* Mobile filter bar — wraps below the tabs, no horizontal scrolling */}
        <div className="flex sm:hidden items-center gap-2 flex-wrap mt-2" data-testid="task-mobile-filterbar">
          {filterCluster}
        </div>
        {/* BUG-10 — By Client scope selector. Owner/Admin/Manager pick scope;
            Team is forced to "my" (no UI). Subtitle below the tabs makes the
            active scope visible at all times. */}
        {view === 'client' && (
          <div className="flex items-center gap-3 mt-2" data-testid="by-client-scope-row">
            <span className="text-[11px] text-muted-foreground/50">Scope:</span>
            <div className="flex items-center rounded-lg border border-border/20 bg-foreground/[0.02] p-0.5" data-testid="by-client-scope-toggle">
              {[
                { id: 'my',   label: 'My Tasks', show: true },
                { id: 'team', label: 'Team',     show: canViewAll || isManager },
                { id: 'all',  label: 'All',      show: canViewAll },
              ].filter(o => o.show).map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setClientScopePersist(opt.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${clientScope === opt.id ? 'bg-gold/15 text-gold' : 'text-muted-foreground/50 hover:text-foreground'}`}
                  data-testid={`by-client-scope-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <span className="text-[11px] text-muted-foreground/50" data-testid="by-client-scope-subtitle">
              Showing tasks for:
              <span className="ml-1 text-foreground font-medium">
                {clientScope === 'my' ? 'My Tasks' : clientScope === 'team' ? 'Team (everyone)' : 'All'}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold/40" /></div>
        ) : viewMode === 'board' ? (
          /* ── BOARD VIEW (toggle) ── */
          /* Time-window filter applies; overdue tasks live in the strip. */
          <div className="flex flex-col gap-3" style={{ minHeight: '500px' }}>
            {overdueStripEl}
            {/* C14 GT-007 — hidden-task banner. Shown when the active
                time-window filter hides ≥1 task from the board so users
                aren't silently missing rows. "Clear date filter" toggles
                the window to "all" without changing tabs. */}
            {(() => {
              const totalForView = viewTasks.length;
              const visible = renderedCount;
              const hidden = Math.max(0, totalForView - visible);
              if (timeWindow === 'all' || hidden === 0) return null;
              const windowLabel = timeWindow === 'day' ? 'today' : timeWindow === 'week' ? 'last 7 days' : timeWindow === 'month' ? 'this month' : 'this window';
              return (
                <div
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-500/[0.06] border border-amber-500/20 text-[12px] text-amber-200/90"
                  data-testid="board-hidden-banner"
                >
                  <Filter className="h-3.5 w-3.5 text-amber-300/70 shrink-0" />
                  <span>
                    Showing tasks from <span className="font-semibold">{windowLabel}</span> · <span className="font-semibold">{hidden}</span> hidden
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTimeWindowPersist('all')}
                    className="h-6 px-2 ml-auto text-[11px] text-amber-200 hover:text-amber-100 hover:bg-amber-500/[0.12]"
                    data-testid="board-clear-date-filter"
                  >
                    Clear date filter
                  </Button>
                </div>
              );
            })()}
            <DndContext sensors={sensors} onDragEnd={handleDragEnd} accessibility={{ announcements: { onDragStart: ({ active }) => `Picked up task ${active.id}`, onDragOver: ({ over }) => over ? `Hovering over ${over.id}` : 'No drop target', onDragEnd: ({ over }) => over ? `Dropped into ${over.id}` : 'Drag cancelled', onDragCancel: () => 'Drag cancelled' } }}>
            <div className="flex gap-3 overflow-x-auto pb-4 flex-1">
            {BOARD_COLS.map(col => {
              const inCol = (t) => (t.status === 'waiting' ? 'in_progress' : t.status) === col.status;
              // 2026-07 — overdue tasks live in the collapsible strip above.
              const colTasks = windowFiltered.filter(inCol);
              // C14 GT-007 — "visible / total" column tooltip. Total counts
              // every task in this view at this status (ignoring the time
              // window) so users see what got filtered out.
              const colTotal = viewTasks.filter(inCol).length;
              const colTooltip = `${colTasks.length} visible / ${colTotal} total ${col.label}`;
              return (
                <BoardDroppableColumn key={col.status} id={col.status} className={`flex-1 min-w-[220px] rounded-xl border ${col.border} ${col.bg} flex flex-col`}>
                  <div
                    className="px-3 py-2.5 border-b border-border/10 flex items-center gap-2"
                    title={colTooltip}
                    aria-label={colTooltip}
                    data-testid={`board-col-header-${col.status}`}
                  >
                    <span className={`h-2 w-2 rounded-full ${col.dot}`} />
                    <span className="text-[12px] font-semibold text-foreground">{col.label}</span>
                    <span className="text-[11px] text-muted-foreground/30">
                      ({colTasks.length}{colTotal > colTasks.length ? ` / ${colTotal}` : ''})
                    </span>
                  </div>
                  <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
                    {colTasks.map(t => (
                      <BoardDraggableCard
                        key={t.id}
                        id={t.id}
                        onOpen={() => setSelectedTaskId(t.id)}
                        className="p-2.5 rounded-lg bg-card border border-border/20 cursor-pointer hover:border-border/40 relative"
                      >
                        <div className="flex items-start gap-1.5">
                          <p className="text-[12px] font-medium text-foreground leading-snug flex-1">{t.title}</p>
                          <PendingTransferBell task={t} currentUserId={user?.id} /><SubtaskCountBadge task={t} />
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {t.status !== 'completed' && t.status !== 'cancelled' && (
                            <>
                              <span className={`h-1.5 w-1.5 rounded-full ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium}`} />
                              {t.priority_locked && <Lock className="h-2.5 w-2.5 text-muted-foreground/25" />}
                            </>
                          )}
                          <TaskStatusIndicator status={t.status} size="xs" />
                          {t.due_date && <span className="text-[9px] text-muted-foreground/40 ml-auto">{formatDate(t.due_date)}</span>}
                        </div>
                      </BoardDraggableCard>
                    ))}
                    {colTasks.length === 0 && <p className="text-[11px] text-muted-foreground/20 text-center py-6">Empty</p>}
                  </div>
                </BoardDroppableColumn>
              );
            })}
            </div>
            </DndContext>
          </div>
        ) : (
          /* ── LIST VIEW (sections + due-date buckets) ── */
          <div className="space-y-3">
            {/* 2026-09 — tasks are NEVER deleted by filters; surface what's hidden */}
            {!showCompleted && (viewTasks.length - windowFiltered.length - overdueTasks.length) > 0 && (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground/60 px-1" data-testid="hidden-by-filters-notice">
                <Filter className="h-3 w-3" />
                <span>{viewTasks.length - windowFiltered.length - overdueTasks.length} task{(viewTasks.length - windowFiltered.length - overdueTasks.length) === 1 ? ' is' : 's are'} hidden by current filters.</span>
                <button onClick={() => { setTimeWindowPersist('all'); setShowCompleted(false); }} className="text-gold hover:underline font-medium" data-testid="clear-filters-btn">Clear filters</button>
              </div>
            )}
            {/* Single collapsible overdue strip — collapsed by default */}
            {overdueStripEl}

            {/* Empty state — no overdue + no in-window tasks */}
            {windowEmpty ? (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="task-window-empty">
                {/* Phase 2 Group B4 — Manager-specific hint when there are
                    no direct reports linked via manager_id. */}
                {view === 'team_mine' && directReports && directReports.length === 0 ? (
                  <>
                    <p className="text-[13px] text-muted-foreground/60">No direct reports assigned to you yet.</p>
                    <p className="text-[11px] text-muted-foreground/40 mt-1">An Owner or Admin can link team members to you in Settings → Members.</p>
                  </>
                ) : (
                  <p className="text-[13px] text-muted-foreground/60">
                    {timeWindow === 'all' ? 'No tasks here yet.' : `No tasks due ${timeWindow === 'day' ? 'today' : timeWindow === 'week' ? 'this week' : 'this month'}.`}
                  </p>
                )}
                {noDueDateCount > 0 && timeWindow !== 'all' && (
                  <button
                    onClick={() => setTimeWindowPersist('all')}
                    className="text-[11px] text-gold/70 hover:text-gold underline-offset-2 hover:underline mt-2"
                    data-testid="task-window-empty-show-all"
                  >
                    {noDueDateCount} task{noDueDateCount === 1 ? '' : 's'} without a due date — click to show all
                  </button>
                )}
                {canViewAll && view !== 'team' && (
                  <Button variant="outline" size="sm" className="h-8 text-[11px] mt-4 border-border/30" onClick={() => setViewPersist('team')} data-testid="task-window-empty-all">
                    Switch to Team view
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {sections.filter(g => g.tasks.length > 0 || g.alwaysShow).map((g, gIdx) => (
              <div key={g.key} data-testid={`task-group-${g.key}`}>
                {(sections.length > 1 || view === 'client') && (
                  <button
                    onClick={() => toggleGroup(g.key)}
                    className={`flex items-center gap-2.5 w-full py-3 px-1 ${gIdx > 0 ? 'border-t border-border/30' : ''} group/header`}
                    data-testid={`task-group-toggle-${g.key}`}
                  >
                    <span
                      className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-border/30 bg-foreground/[0.02] text-muted-foreground/70 transition-all group-hover/header:border-gold/40 group-hover/header:bg-foreground/[0.06] group-hover/header:text-gold"
                      aria-hidden="true"
                    >
                      <span className={`transition-transform duration-150 ${collapsed[g.key] ? '' : 'rotate-90'}`}>
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </span>
                    {g.color && <span className={`h-2.5 w-2.5 rounded-full ${g.color}`} />}
                    <span className="text-[16px] sm:text-[18px] font-display font-semibold text-foreground tracking-tight">{g.label}</span>
                    <span className="text-[11px] text-muted-foreground/50 ml-1">({g.tasks.length})</span>
                  </button>
                )}
                {!collapsed[g.key] && (
                  g.tasks.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground/40 pl-3 py-2" data-testid={`task-group-empty-${g.key}`}>
                      No tasks here.
                    </p>
                  ) : (
                    <div className="pl-2 pt-1 space-y-2">
                      {(groupBy === 'priority' ? priorityGroups(g.tasks) : bucketize(g.tasks)).map(b => (
                        <div key={b.key} data-testid={`task-bucket-${g.key}-${b.key}`}>
                          <div className="flex items-center gap-2 pt-1 pb-1 pl-1">
                            {b.color && <span className={`h-1.5 w-1.5 rounded-full ${b.color}`} />}
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/50">{b.label}</span>
                            <span className="text-[10px] text-muted-foreground/30">({b.tasks.length})</span>
                          </div>
                          <div className="space-y-0.5">{b.tasks.map(t => renderRow(t))}</div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ))}
            {nextCursor && <div className="text-center py-4"><Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Loading...' : `Load more (${tasks.length} of ${totalTasks})`}</Button></div>}
              </div>
            )}

            {/* No-due-date hint at the bottom (only when there are tasks shown above and we're in a time-window view) */}
            {!windowEmpty && noDueDateCount > 0 && timeWindow !== 'all' && (
              <div className="text-center py-3" data-testid="task-no-duedate-hint">
                <button
                  onClick={() => setTimeWindowPersist('all')}
                  className="text-[11px] text-gold/70 hover:text-gold underline-offset-2 hover:underline"
                  data-testid="task-no-duedate-show-all"
                >
                  {noDueDateCount} task{noDueDateCount === 1 ? '' : 's'} without a due date — click to show all
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── QUICK CREATE DIALOG — Batch 3 B8 unified ── */}
      <TaskCreateModal
        open={showCreate}
        onOpenChange={(o) => { setShowCreate(o); if (!o) setCreateDefaultDueDate(''); }}
        workspaceId={wsId}
        members={members}
        clients={clients}
        currentUserId={user?.id}
        defaultDueDate={createDefaultDueDate}
        onCreated={() => fetchTasks()}
      />

      <TaskDetailSheet taskId={selectedTaskId} open={!!selectedTaskId} focusSubmissionId={focusSubmission} focusCommentId={focusComment} onOpenChange={o => { if (!o) { setSelectedTaskId(null); setFocusSubmission(null); setFocusComment(null); } }} onUpdate={fetchTasks} />
    </div>
  );
}
